import React, { useState } from 'react';
import { PluginInterface, PluginConfigProps, PluginPreviewProps } from '../types/PluginInterface';
import { SurveyConfig, SurveyQuestion } from '../types/PluginTypes';
import SurveyPlugin from './SurveyPlugin';
import SurveyResults from './components/SurveyResults'; // ✅ IMPORT DU NOUVEAU COMPOSANT
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import { Editor } from '@tinymce/tinymce-react';
import { withTabLink } from '../utils/tinymceTabLink';
import { useTinyMceApiKey } from '../hooks/useTinyMceApiKey';
import { apiKeyService } from '../services/apiKeyService';

// ✅ Extension locale de l'interface pour inclure les nouveaux champs de style
interface ExtendedSurveyConfig extends SurveyConfig {
  textColor?: string;
  responsive?: boolean;
  backgroundColor?: string;
  backgroundTransparent?: boolean;
  borderRadius?: number;
  enableAntiSpam?: boolean;
  antiSpamMinSeconds?: number;
  requireCaptcha?: boolean;
  expirationDate?: Date;
  notificationEmail?: string;
  mode?: 'survey' | 'game' | 'exam';
  pointsGoal?: number;
  examScoreScale?: number;
  uiStylePrompt?: string;
  uiStyleModel?: string;
}

// Configuration par défaut avec nouvelles fonctionnalités
// ✅ Utilisation du type étendu pour éviter l'erreur "Object literal..."
const defaultConfig: ExtendedSurveyConfig = {
  title: 'Nouveau Questionnaire',
  description: 'Aidez-nous en répondant à quelques questions.',
  questions: [
    {
      id: 'question_1',
      type: 'text' as const,
      title: 'Comment vous appelez-vous ?',
      required: true,
      placeholder: 'Votre nom...',
      analytics: {
        trackTime: true,
        trackInteractions: true
      }
    },
    {
      id: 'question_2',
      type: 'rating' as const,
      title: 'Comment évaluez-vous notre service ?',
      required: true,
      ratingMax: 5,
      analytics: {
        trackTime: true,
        trackInteractions: true
      }
    }
  ],
  submitButtonText: 'Envoyer mes réponses',
  thankYouMessage: 'Merci pour vos réponses ! Elles nous aident à nous améliorer.',
  allowMultipleSubmissions: false,
  showProgressBar: true,
  
  // ✅ CORRECTION : Ces champs sont obligatoires dans le type SurveyConfig
  // On les garde dans la config par défaut pour satisfaire TypeScript, même si on ne les affiche plus
  theme: 'default',
  primaryColor: '#0d6efd',
  
  textColor: '#333333',
  responsive: true,
  backgroundColor: '#ffffff',
  backgroundTransparent: false,
  borderRadius: 0,
  mode: 'survey',
  pointsGoal: 10,
  examScoreScale: 20,
  uiStylePrompt: '',
  uiStyleModel: '',
  
  collectEmail: false,
  enableAnalytics: true,
  enableExport: true,
  autoSave: false,
  enableNotifications: false,
  globalValidation: {
    enableSpamProtection: true,
    requireCaptcha: false,
    duplicateSubmissionCheck: true
  }
};

// Composant de configuration du plugin
const SurveyConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  // ✅ Cast typé pour faciliter l'accès aux propriétés étendues
  const currentConfig = config as ExtendedSurveyConfig;
  const [activeTab, setActiveTab] = useState('general');
  const { apiKey: tinymceApiKey, isLoading: tinymceLoading, error: tinymceError } = useTinyMceApiKey();
  
  // ✅ NOUVEAU : État pour stocker les erreurs de validation
  const [validationErrors, setValidationErrors] = useState<{ id: string, message: string }[]>([]);
  const scoringEnabled = (currentConfig.mode || 'survey') !== 'survey';
  const [availablePromptModels, setAvailablePromptModels] = useState<Array<{ id: string; label: string; provider: 'OpenAI' | 'GoogleAI' | 'LTX' }>>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);

  const updateConfig = (updates: Partial<ExtendedSurveyConfig>) => {
    onConfigChange({ ...config, ...updates });
  };

  // Associer le propriétaire au plugin
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    if ((currentConfig as any).userId || (currentConfig as any).ownerId || (currentConfig as any).ownerEmail) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      const ownerEmail = userData?.email;
      if (ownerId || ownerEmail) {
        onConfigChange({
          ...config,
          ...(ownerId ? { userId: String(ownerId) } : {}),
          ...(ownerEmail ? { ownerEmail } : {})
        });
      }
    } catch {
      // ignore
    }
  }, [config, currentConfig, onConfigChange]);

  React.useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      setIsLoadingModels(true);
      setModelsError(null);
      try {
        const keys = await apiKeyService.getApiKeys();
        const hasOpenAI = keys.some((k: any) => k.service === 'OpenAI' && k.isActive);
        const hasGoogleAI = keys.some((k: any) => k.service === 'GoogleAI' && k.isActive);
        const hasLTX = keys.some((k: any) => k.service === 'LTX' && k.isActive);

        const promptModels = [
          ...(hasOpenAI ? [{ id: 'dall-e-3', label: 'DALL·E 3 (image)', provider: 'OpenAI' as const }] : []),
          ...(hasGoogleAI ? [{ id: 'imagen-4.0-generate-001', label: 'Imagen 4 (image)', provider: 'GoogleAI' as const }] : []),
          ...(hasOpenAI ? [{ id: 'sora-2', label: 'Sora 2 (vidéo)', provider: 'OpenAI' as const }] : []),
          ...(hasGoogleAI ? [{ id: 'veo-3.1-generate-preview', label: 'Veo 3.1 (vidéo)', provider: 'GoogleAI' as const }] : []),
          ...(hasLTX ? [
            { id: 'ltx-2-fast', label: 'LTX 2 Fast (vidéo)', provider: 'LTX' as const },
            { id: 'ltx-2-pro', label: 'LTX 2 Pro (vidéo)', provider: 'LTX' as const }
          ] : [])
        ];

        if (!cancelled) {
          setAvailablePromptModels(promptModels);
          if (!currentConfig.uiStyleModel || !promptModels.some(m => m.id === currentConfig.uiStyleModel)) {
            updateConfig({ uiStyleModel: promptModels[0]?.id || '' });
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          setAvailablePromptModels([]);
          setModelsError(error?.message || "Impossible de charger vos clés IA.");
        }
      } finally {
        if (!cancelled) setIsLoadingModels(false);
      }
    };

    loadModels();
    return () => { cancelled = true; };
  }, []);

  // ✅ NOUVEAU : Système de validation en temps réel
  React.useEffect(() => {
    const errors: { id: string, message: string }[] = [];
    const questions = currentConfig.questions || [];
    const questionIds = new Set(questions.map(q => q.id));

    questions.forEach((q, index) => {
      // 1. Vérifier le titre vide
      if (!q.title || !q.title.trim()) {
        errors.push({ id: q.id, message: `Question ${index + 1} : Le titre est requis.` });
      }

      // 2. Vérifier les options pour les types à choix
      if (['radio', 'checkbox', 'select'].includes(q.type)) {
        if (!q.options || q.options.length === 0 || q.options.every(opt => !opt.trim())) {
          errors.push({ id: q.id, message: `Question ${index + 1} : Au moins une option est requise.` });
        }
      }

      // 3. Vérifier les lignes/colonnes pour les matrices
      if (q.type === 'matrix') {
        if (!q.matrixRows || q.matrixRows.length === 0) errors.push({ id: q.id, message: `Question ${index + 1} : Lignes manquantes.` });
        if (!q.matrixColumns || q.matrixColumns.length === 0) errors.push({ id: q.id, message: `Question ${index + 1} : Colonnes manquantes.` });
      }

      // 4. Vérifier la logique conditionnelle brisée (pointe vers une question supprimée)
      if (q.conditionalLogic?.dependsOn) {
        if (!questionIds.has(q.conditionalLogic.dependsOn)) {
          errors.push({ id: q.id, message: `Question ${index + 1} : La condition dépend d'une question introuvable (peut-être supprimée).` });
        }
        // Empêcher l'auto-référence
        if (q.conditionalLogic.dependsOn === q.id) {
           errors.push({ id: q.id, message: `Question ${index + 1} : Une question ne peut pas dépendre d'elle-même.` });
        }
      }
    });

    setValidationErrors(errors);
  }, [currentConfig.questions]);

  // Helper pour savoir si une question spécifique a une erreur
  const getQuestionError = (qId: string) => validationErrors.find(e => e.id === qId);

  const addQuestion = () => {
    const newQuestion: SurveyQuestion = {
      id: `question_${Date.now()}`,
      type: 'text',
      title: 'Nouvelle question',
      required: false,
      placeholder: '',
      analytics: {
        trackTime: true,
        trackInteractions: true
      }
    };
    const questions = [...(currentConfig.questions || []), newQuestion];
    updateConfig({ questions });
  };

  const updateQuestion = (index: number, updates: Partial<SurveyQuestion>) => {
    const questions = [...(currentConfig.questions || [])];
    const nextQuestion = { ...questions[index], ...updates };

    if (updates.options) {
      const cleanOptions = updates.options.map(opt => opt.trim()).filter(Boolean);
      if (nextQuestion.correctAnswers) {
        nextQuestion.correctAnswers = nextQuestion.correctAnswers.filter(ans => cleanOptions.includes(ans));
      }
    }

    questions[index] = nextQuestion;
    updateConfig({ questions });
  };

  const removeQuestion = (index: number) => {
    const questions = (currentConfig.questions || []).filter((_: SurveyQuestion, i: number) => i !== index);
    updateConfig({ questions });
  };

  return (
    <div className="p-3">
      {/* ✅ NOUVEAU : Affichage des erreurs bloquantes en haut du panneau */}
      {validationErrors.length > 0 && (
        <div className="alert alert-danger mb-3">
          <h6 className="alert-heading small fw-bold mb-1">⚠️ Configuration incomplète</h6>
          <ul className="mb-0 small ps-3">
            {validationErrors.slice(0, 3).map((err, idx) => (
              <li key={idx}>{err.message}</li>
            ))}
            {validationErrors.length > 3 && <li>Et {validationErrors.length - 3} autre(s) erreur(s)...</li>}
          </ul>
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            Général
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'questions' ? 'active' : ''} ${validationErrors.length > 0 ? 'text-danger' : ''}`}
            onClick={() => setActiveTab('questions')}
          >
            Questions {validationErrors.length > 0 && '⚠️'}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'advanced' ? 'active' : ''}`}
            onClick={() => setActiveTab('advanced')}
          >
            Fonctionnalités
          </button>
        </li>
        
        {/* ✅ NOUVEL ONGLET RÉSULTATS (Visible si Analytics activé) */}
        {currentConfig.enableAnalytics && (
          <li className="nav-item">
            <button
               className={`nav-link ${activeTab === 'results' ? 'active' : ''}`}
               onClick={() => setActiveTab('results')}
            >
              Résultats 📊
            </button>
          </li>
        )}
      </ul>

      <div className="tab-content">
        {/* ========================================================================== */}
        {/* ONGLET "GÉNÉRAL"                                                           */}
        {/* ========================================================================== */}
        {activeTab === 'general' && (
          <div>
            {/* Titre et description */}
            <div className="mb-3">
              <label className="form-label">Titre du questionnaire</label>
              <input
                type="text"
                className="form-control"
                value={currentConfig.title || ''}
                onChange={(e) => updateConfig({ title: e.target.value })}
                placeholder="Ex: Questionnaire de satisfaction"
              />
            </div>
            
            <div className="mb-3">
              <label className="form-label">Description</label>
              {tinymceLoading ? (
                <div>Chargement...</div>
              ) : !tinymceApiKey ? (
                <>
                  <div className="alert alert-warning py-2">Clé TinyMCE manquante</div>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={currentConfig.description || ''}
                    onChange={(e) => updateConfig({ description: e.target.value })}
                    placeholder="Une brève description du questionnaire..."
                  />
                </>
              ) : (
                <Editor
                  apiKey={tinymceApiKey}
                  value={currentConfig.description || ''}
                  onEditorChange={(content) => updateConfig({ description: content })}
                  init={withTabLink({
                    height: 200,
                    menubar: false,
                    plugins: [
                      'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
                      'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'wordcount'
                    ],
                    toolbar: 'undo redo | fontfamily fontsize | forecolor backcolor | formatselect | bold italic underline strikethrough | ' +
                      'alignleft aligncenter alignright alignjustify | ' +
                      'bullist numlist outdent indent | removeformat | ' +
                      'link image media | table | code fullscreen',
                    font_family_formats: 'Arial=arial,helvetica,sans-serif;Times New Roman=times new roman,times;Courier New=courier new,courier;Georgia=georgia,palatino;Verdana=verdana,geneva',
                    content_style: `
                      body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                      img[style*="float: left"] { margin: 0 12px 12px 0; }
                      img[style*="float: right"] { margin: 0 0 12px 12px; }
                    `,
                    language: 'fr',
                    branding: false,
                    allow_script_urls: true,
                    allow_html_data_urls: true,
                    allow_events: true,
                    extended_valid_elements: 'div[*],span[*],i[*],button[*]',
                    custom_elements: 'button,i',
                    valid_children: '+body[div|button]',
                    valid_elements: '*[*]',
                    paste_data_images: true,
                    paste_as_text: false,
                    paste_retain_style_properties: 'all'
                  })}
                />
              )}
              {tinymceError && <div className="text-danger small mt-1">{tinymceError}</div>}
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label">Mode</label>
                <select
                  className="form-select"
                  value={currentConfig.mode || 'survey'}
                  onChange={(e) => updateConfig({ mode: e.target.value as 'survey' | 'game' | 'exam' })}
                >
                  <option value="survey">Sondage</option>
                  <option value="game">Jeu</option>
                  <option value="exam">Examen</option>
                </select>
              </div>
              {currentConfig.mode === 'game' && (
                <div className="col-md-4">
                  <label className="form-label">Objectif de points</label>
                  <input
                    type="number"
                    className="form-control"
                    min={0}
                    value={currentConfig.pointsGoal ?? 0}
                    onChange={(e) => updateConfig({ pointsGoal: Math.max(0, Number(e.target.value || 0)) })}
                  />
                </div>
              )}
              {currentConfig.mode === 'exam' && (
                <div className="col-md-4">
                  <label className="form-label">Note sur</label>
                  <input
                    type="number"
                    className="form-control"
                    min={1}
                    value={currentConfig.examScoreScale ?? 20}
                    onChange={(e) => updateConfig({ examScoreScale: Math.max(1, Number(e.target.value || 1)) })}
                  />
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Prompt AI (style visuel)</label>
              <textarea
                className="form-control"
                rows={2}
                value={currentConfig.uiStylePrompt || ''}
                onChange={(e) => updateConfig({ uiStylePrompt: e.target.value })}
                placeholder="Ex: mode jeux enfant, couleurs vives, thème spatial, boutons arrondis..."
              />
              <small className="text-muted">Ce prompt sert à guider l’apparence (mode jeu/examen).</small>
            </div>

            <div className="mb-3">
              <label className="form-label">Modèle IA</label>
              {isLoadingModels ? (
                <div className="text-muted small">Chargement des modèles...</div>
              ) : availablePromptModels.length === 0 ? (
                <div className="text-muted small">
                  Aucun modèle disponible. Ajoutez une clé OpenAI, GoogleAI ou LTX dans vos paramètres.
                </div>
              ) : (
                <select
                  className="form-select"
                  value={currentConfig.uiStyleModel || ''}
                  onChange={(e) => updateConfig({ uiStyleModel: e.target.value })}
                >
                  {availablePromptModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.label}
                    </option>
                  ))}
                </select>
              )}
              {modelsError && <div className="text-danger small mt-1">{modelsError}</div>}
            </div>

            {/* Message de remerciement et Texte du bouton */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Message de remerciement</label>
                <input
                  type="text"
                  className="form-control"
                  value={currentConfig.thankYouMessage || ''}
                  onChange={(e) => updateConfig({ thankYouMessage: e.target.value })}
                  placeholder="Ex: Merci pour vos réponses !"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Texte du bouton</label>
                <input
                  type="text"
                  className="form-control"
                  value={currentConfig.submitButtonText || ''}
                  onChange={(e) => updateConfig({ submitButtonText: e.target.value })}
                  placeholder="Ex: Envoyer mes réponses"
                />
              </div>
            </div>

            {/* ✅ NOUVELLE SECTION STYLE (Remplace Thème et Couleur primaire) */}
            <hr className="my-3" />
            <h6 className="mb-3">🎨 Apparence du formulaire</h6>
            
            <div className="row g-3 mb-3">
               <div className="col-md-6">
                  <label className="form-label">Couleur du texte</label>
                  <input
                    type="color"
                    className="form-control form-control-color w-100"
                    value={currentConfig.textColor || '#333333'}
                    onChange={(e) => updateConfig({ textColor: e.target.value })}
                  />
               </div>
               <div className="col-md-6">
                 <label className="form-label">Couleur de fond</label>
                 <div className="input-group">
                    <div className="input-group-text">
                      <input 
                        className="form-check-input mt-0" 
                        type="checkbox" 
                        checked={currentConfig.backgroundTransparent === true}
                        onChange={(e) => updateConfig({ backgroundTransparent: e.target.checked })}
                        title="Fond transparent"
                      />
                    </div>
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={currentConfig.backgroundColor || '#ffffff'}
                      onChange={(e) => updateConfig({ backgroundColor: e.target.value, backgroundTransparent: false })}
                      disabled={currentConfig.backgroundTransparent === true}
                      title="Couleur de fond"
                    />
                 </div>
                 {currentConfig.backgroundTransparent && <small className="text-muted d-block mt-1">Fond transparent activé</small>}
               </div>
            </div>

            <div className="row g-3">
               <div className="col-md-6">
                 <label className="form-label">Arrondi du cadre</label>
                 <div className="d-flex align-items-center gap-2">
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="30"
                      value={currentConfig.borderRadius || 0}
                      onChange={(e) => updateConfig({ borderRadius: parseInt(e.target.value) })}
                    />
                    <span className="badge bg-light text-dark border">
                        {currentConfig.borderRadius || 0}px
                    </span>
                 </div>
               </div>
               
               <div className="col-md-6">
                 <div className="form-check form-switch mt-4">
                   <input
                     className="form-check-input"
                     type="checkbox"
                     checked={currentConfig.responsive ?? true}
                     onChange={(e) => updateConfig({ responsive: e.target.checked })}
                   />
                   <label className="form-check-label">Affichage responsive (100%)</label>
                 </div>
               </div>
            </div>

            {/* Options diverses */}
            <div className="form-check mb-2 mt-3 pt-3 border-top">
              <input
                className="form-check-input"
                type="checkbox"
                checked={currentConfig.showProgressBar || false}
                onChange={(e) => updateConfig({ showProgressBar: e.target.checked })}
              />
              <label className="form-check-label">
                Afficher la barre de progression
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={currentConfig.allowMultipleSubmissions || false}
                onChange={(e) => updateConfig({ allowMultipleSubmissions: e.target.checked })}
              />
              <label className="form-check-label">
                Autoriser plusieurs soumissions
              </label>
            </div>
          </div>
        )}

        {/* ========================================================================== */}
        {/* ONGLET "QUESTIONS"                                                         */}
        {/* ========================================================================== */}
        {activeTab === 'questions' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">📝 Questions ({(currentConfig.questions || []).length})</h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={addQuestion}
              >
                + Ajouter une question
              </button>
            </div>
            <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0 10px 10px 5px' }}>
              {(currentConfig.questions || []).map((question: SurveyQuestion, index: number) => {
                // ✅ Vérifier si cette question a une erreur
                const error = getQuestionError(question.id);
                
                return (
                <div key={question.id} className={`card mb-3 ${error ? 'border-danger' : ''}`}>
                  {/* Si erreur, afficher un petit bandeau rouge dans la carte */}
                  {error && (
                    <div className="card-header bg-danger text-white py-1 small">
                      <i className="fas fa-exclamation-triangle me-1"></i> {error.message}
                    </div>
                  )}

                  <div className={`card-header d-flex justify-content-between align-items-center ${error ? 'bg-opacity-10 bg-danger' : ''}`}>
                    <small className={error ? 'text-danger fw-bold' : 'text-muted'}>Question {index + 1}</small>
                    <div className="d-flex gap-2">
                      {question.conditionalLogic && (
                        <span className="badge bg-info" title="Logique conditionnelle active">
                          🔗 Conditionnelle
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeQuestion(index)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">Titre de la question <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control ${!question.title?.trim() ? 'is-invalid' : ''}`}
                        value={question.title}
                        onChange={(e) => updateQuestion(index, { title: e.target.value })}
                        placeholder="Intitulé de la question..."
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description (optionnelle)</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={question.description || ''}
                        onChange={(e) => updateQuestion(index, { description: e.target.value })}
                        placeholder="Informations supplémentaires pour aider à répondre..."
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">Type de question</label>
                        <select
                          className="form-select"
                          value={question.type}
                          onChange={(e) => updateQuestion(index, { type: e.target.value as any })}
                        >
                          <option value="text">Texte court</option>
                          <option value="textarea">Texte long</option>
                          <option value="email">Email</option>
                          <option value="phone">Téléphone</option>
                          <option value="url">URL/Site web</option>
                          <option value="number">Nombre</option>
                          <option value="date">Date</option>
                          <option value="rating">Note (étoiles)</option>
                          <option value="scale">Échelle (1-10)</option>
                          <option value="radio">Choix unique</option>
                          <option value="checkbox">Choix multiple</option>
                          <option value="select">Liste déroulante</option>
                          <option value="matrix">Matrice (tableau)</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check mt-4">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={question.required || false}
                            onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                          />
                          <label className="form-check-label">
                            Question obligatoire
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Configuration spécifique par type */}
                    {(question.type === 'radio' || question.type === 'checkbox' || question.type === 'select') && (
                      <div className="mt-3">
                        <label className="form-label">Options (une par ligne) <span className="text-danger">*</span></label>
                        <textarea
                          className={`form-control ${(!question.options || question.options.length === 0) ? 'is-invalid' : ''}`}
                          rows={3}
                          value={(question.options || []).join('\n')}
                          onChange={(e) => {
                            const options = e.target.value.split('\n'); // On garde les lignes vides pendant la saisie pour l'UX
                            // On filtre seulement au moment de l'update pour le state propre si besoin, 
                            // mais ici on stocke brut pour l'édition et on valide visuellement
                            const cleanOptions = e.target.value.split('\n').map(s => s.trim()).filter(s => s !== ''); 
                            // Astuce: pour l'UX textarea, on doit stocker ce que l'user tape, 
                            // donc on reconstruit le tableau depuis la valeur brute du textarea dans l'event onChange
                            // Mais ici le code existant faisait .filter(opt => opt.trim()).
                            // On va améliorer ça :
                            updateQuestion(index, { options: e.target.value.split('\n') });
                          }}
                          // Note: le .join('\n') affichera les options.
                          placeholder="Option 1&#10;Option 2&#10;Option 3"
                        />
                         {(!question.options || question.options.filter(o => o.trim()).length === 0) && (
                           <div className="invalid-feedback d-block">Veuillez ajouter au moins une option.</div>
                         )}
                      </div>
                    )}

                    {scoringEnabled && (question.type === 'radio' || question.type === 'checkbox' || question.type === 'select') && (
                      <div className="mt-3 p-2 rounded border bg-light">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">🎯 Scoring</h6>
                          <small className="text-muted">Bonnes réponses + points</small>
                        </div>
                        <div className="row g-2 align-items-end">
                          {question.type !== 'checkbox' && (
                            <div className="col-md-3">
                              <label className="form-label">Points</label>
                              <input
                                type="number"
                                className="form-control"
                                min={0}
                                value={question.points ?? 1}
                                onChange={(e) => updateQuestion(index, { points: Math.max(0, Number(e.target.value || 0)) })}
                              />
                            </div>
                          )}
                          <div className="col-md-9">
                            <label className="form-label">Bonne(s) réponse(s)</label>
                            {(() => {
                              const cleanOptions = (question.options || []).map(opt => opt.trim()).filter(Boolean);
                              if (cleanOptions.length === 0) {
                                return <div className="text-muted small">Ajoutez des options pour sélectionner la(les) bonne(s) réponse(s).</div>;
                              }
                              if (question.type === 'checkbox') {
                                const currentCorrect = question.correctAnswers || [];
                                return (
                                  <div className="d-flex flex-wrap gap-2">
                                    {cleanOptions.map((opt) => (
                                      <label key={opt} className="form-check form-check-inline">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={currentCorrect.includes(opt)}
                                          onChange={(e) => {
                                            const nextCorrect = e.target.checked
                                              ? [...currentCorrect, opt]
                                              : currentCorrect.filter(ans => ans !== opt);
                                            updateQuestion(index, { correctAnswers: nextCorrect });
                                          }}
                                        />
                                        <span className="form-check-label">{opt}</span>
                                      </label>
                                    ))}
                                  </div>
                                );
                              }

                              return (
                                <select
                                  className="form-select"
                                  value={question.correctAnswers?.[0] || ''}
                                  onChange={(e) => updateQuestion(index, { correctAnswers: e.target.value ? [e.target.value] : [] })}
                                >
                                  <option value="">Sélectionner...</option>
                                  {cleanOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            })()}
                          </div>
                        </div>
                        {question.type === 'checkbox' && (
                          <div className="mt-3 border-top pt-2">
                            <div className="row g-2 align-items-end">
                              <div className="col-md-4">
                                <label className="form-label">Barème</label>
                                <select
                                  className="form-select"
                                  value={question.scoringMode || 'all_or_nothing'}
                                  onChange={(e) => updateQuestion(index, { scoringMode: e.target.value as 'all_or_nothing' | 'partial' })}
                                >
                                  <option value="all_or_nothing">Tout ou rien</option>
                                  <option value="partial">Partiel (+/-)</option>
                                </select>
                              </div>
                              {question.scoringMode !== 'partial' ? (
                                <div className="col-md-4">
                                  <label className="form-label">Points</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    min={0}
                                    value={question.points ?? 1}
                                    onChange={(e) => updateQuestion(index, { points: Math.max(0, Number(e.target.value || 0)) })}
                                  />
                                </div>
                              ) : (
                                <>
                                  <div className="col-md-4">
                                    <label className="form-label">+ par bonne case</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      min={0}
                                      value={question.partialPointsPerCorrect ?? 1}
                                      onChange={(e) => updateQuestion(index, { partialPointsPerCorrect: Math.max(0, Number(e.target.value || 0)) })}
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label">- par mauvaise case</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      min={0}
                                      value={question.partialPointsPerWrong ?? 1}
                                      onChange={(e) => updateQuestion(index, { partialPointsPerWrong: Math.max(0, Number(e.target.value || 0)) })}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {question.type === 'rating' && (
                      <div className="mt-3">
                        <label className="form-label">Nombre d'étoiles maximum</label>
                        <input
                          type="number"
                          className="form-control"
                          min="1"
                          max="10"
                          value={question.ratingMax || 5}
                          onChange={(e) => updateQuestion(index, { ratingMax: parseInt(e.target.value) })}
                        />
                      </div>
                    )}

                    {question.type === 'scale' && (
                      <div className="row mt-3">
                        <div className="col-md-4">
                          <label className="form-label">Valeur minimale</label>
                          <input
                            type="number"
                            className="form-control"
                            value={question.scaleMin || 1}
                            onChange={(e) => updateQuestion(index, { scaleMin: parseInt(e.target.value) })}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Valeur maximale</label>
                          <input
                            type="number"
                            className="form-control"
                            value={question.scaleMax || 10}
                            onChange={(e) => updateQuestion(index, { scaleMax: parseInt(e.target.value) })}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Labels (min/max)</label>
                          <div className="d-flex gap-1">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Min"
                              value={question.scaleLabels?.min || ''}
                              onChange={(e) => updateQuestion(index, { 
                                scaleLabels: { 
                                  min: question.scaleLabels?.min || '',
                                  max: e.target.value 
                                } 
                              })}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Max"
                              value={question.scaleLabels?.max || ''}
                              onChange={(e) => updateQuestion(index, { 
                                scaleLabels: { 
                                  min: question.scaleLabels?.min || '',
                                  max: e.target.value 
                                } 
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {question.type === 'matrix' && (
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <label className="form-label">Lignes (une par ligne)</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={(question.matrixRows || []).join('\n')}
                            onChange={(e) => {
                              const matrixRows = e.target.value.split('\n').filter(row => row.trim());
                              updateQuestion(index, { matrixRows });
                            }}
                            placeholder="Ligne 1&#10;Ligne 2&#10;Ligne 3"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Colonnes (une par ligne)</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={(question.matrixColumns || []).join('\n')}
                            onChange={(e) => {
                              const matrixColumns = e.target.value.split('\n').filter(col => col.trim());
                              updateQuestion(index, { matrixColumns });
                            }}
                            placeholder="Colonne 1&#10;Colonne 2&#10;Colonne 3"
                          />
                        </div>
                      </div>
                    )}

                    {/* Validation avancée */}
                    <div className="mt-3">
                      <h6 className="mb-2">🔧 Validation avancée</h6>
                      <div className="row">
                        {(question.type === 'text' || question.type === 'textarea') && (
                          <>
                            <div className="col-md-6">
                              <label className="form-label small">Nombre de caractères min</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={question.validation?.min || ''}
                                onChange={(e) => updateQuestion(index, { 
                                  validation: { 
                                    ...question.validation, 
                                    min: e.target.value ? parseInt(e.target.value) : undefined 
                                  } 
                                })}
                                placeholder="Ex: 10"
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small">Nombre de caractères max</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={question.validation?.max || ''}
                                onChange={(e) => updateQuestion(index, { 
                                  validation: { 
                                    ...question.validation, 
                                    max: e.target.value ? parseInt(e.target.value) : undefined 
                                  } 
                                })}
                                placeholder="Ex: 500"
                              />
                            </div>
                          </>
                        )}
                        {question.type === 'number' && (
                          <>
                            <div className="col-md-6">
                              <label className="form-label small">Valeur minimale</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={question.validation?.min || ''}
                                onChange={(e) => updateQuestion(index, { 
                                  validation: { 
                                    ...question.validation, 
                                    min: e.target.value ? parseFloat(e.target.value) : undefined 
                                  } 
                                })}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label small">Valeur maximale</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={question.validation?.max || ''}
                                onChange={(e) => updateQuestion(index, { 
                                  validation: { 
                                    ...question.validation, 
                                    max: e.target.value ? parseFloat(e.target.value) : undefined 
                                  } 
                                })}
                              />
                            </div>
                          </>
                        )}
                      </div>
                       <div className="mt-2">
                        <label className="form-label small">Message d'erreur personnalisé</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={question.validation?.customMessage || ''}
                          onChange={(e) => updateQuestion(index, { 
                            validation: { 
                              ...question.validation, 
                              customMessage: e.target.value 
                            } 
                          })}
                          placeholder="Message à afficher en cas d'erreur"
                        />
                      </div>
                     </div>

                     {/* Logique conditionnelle */}
                     <div className="mt-3">
                       <h6 className="mb-2">🔗 Logique conditionnelle</h6>
                       <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!question.conditionalLogic}
                          onChange={(e) => {
                            if (e.target.checked) {
                              updateQuestion(index, {
                                conditionalLogic: {
                                  dependsOn: '',
                                  showIf: 'equals',
                                  value: ''
                                }
                              });
                            } else {
                              updateQuestion(index, { conditionalLogic: undefined });
                            }
                          }}
                        />
                         <label className="form-check-label small">
                          Afficher cette question selon une condition
                        </label>
                       </div>

                       {question.conditionalLogic && (
                        <div className={`ms-3 p-2 rounded ${question.conditionalLogic.dependsOn && !currentConfig.questions.find(q => q.id === question.conditionalLogic?.dependsOn) ? 'bg-danger bg-opacity-10 border border-danger' : 'bg-light'}`}>
                          <div className="row">
                             <div className="col-md-4">
                              <label className="form-label small">Dépend de la question</label>
                              <select
                                className={`form-select form-select-sm ${question.conditionalLogic.dependsOn && !currentConfig.questions.find(q => q.id === question.conditionalLogic?.dependsOn) ? 'is-invalid' : ''}`}
                                value={question.conditionalLogic.dependsOn}
                                onChange={(e) => updateQuestion(index, {
                                  conditionalLogic: {
                                    ...question.conditionalLogic!,
                                    dependsOn: e.target.value
                                  }
                                })}
                              >
                                <option value="">Sélectionner...</option>
                                {currentConfig.questions
                                    .filter(q => q.id !== question.id) // Empêcher l'auto-sélection
                                    .map((q: any) => (
                                  <option key={q.id} value={q.id}>
                                    {q.title ? q.title.substring(0, 30) : `Question ${currentConfig.questions.indexOf(q) + 1}`}...
                                  </option>
                                ))}
                                {/* Si la question a été supprimée mais est toujours référencée, on ajoute une option fantome pour montrer l'erreur */}
                                {question.conditionalLogic.dependsOn && !currentConfig.questions.find(q => q.id === question.conditionalLogic?.dependsOn) && (
                                   <option value={question.conditionalLogic.dependsOn} disabled>❌ Question supprimée</option>
                                )}
                              </select>
                            </div>
                             <div className="col-md-4">
                              <label className="form-label small">Condition</label>
                              <select
                                className="form-select form-select-sm"
                                value={question.conditionalLogic.showIf}
                                onChange={(e) => updateQuestion(index, {
                                  conditionalLogic: {
                                    ...question.conditionalLogic!,
                                    showIf: e.target.value as any
                                  }
                                })}
                              >
                                <option value="equals">Est égal à</option>
                                <option value="not_equals">N'est pas égal à</option>
                                <option value="contains">Contient</option>
                                <option value="greater_than">Supérieur à</option>
                                <option value="less_than">Inférieur à</option>
                                <option value="selected">Contient (choix multiple)</option>
                                <option value="not_selected">Ne contient pas</option>
                              </select>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label small">Valeur</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={question.conditionalLogic.value as string}
                                onChange={(e) => updateQuestion(index, {
                                  conditionalLogic: {
                                    ...question.conditionalLogic!,
                                    value: e.target.value
                                  }
                                })}
                                placeholder="Valeur à comparer"
                              />
                            </div>
                          </div>
                          {question.conditionalLogic.dependsOn && !currentConfig.questions.find(q => q.id === question.conditionalLogic?.dependsOn) && (
                             <small className="text-danger d-block mt-1">La question ciblée n'existe plus.</small>
                          )}
                        </div>
                        )}
                     </div>

                     {/* Analytics pour cette question */}
                     <div className="mt-3">
                       <h6 className="mb-2">📊 Analytics</h6>
                        <div className="row">
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={question.analytics?.trackTime !== false}
                              onChange={(e) => updateQuestion(index, {
                                analytics: {
                                  trackTime: question.analytics?.trackTime !== false,
                                  trackInteractions: e.target.checked
                                }
                              })}
                            />
                            <label className="form-check-label small">
                              Mesurer le temps de réponse
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={question.analytics?.trackInteractions !== false}
                              onChange={(e) => updateQuestion(index, {
                                analytics: {
                                  trackTime: question.analytics?.trackTime !== false,
                                  trackInteractions: e.target.checked
                                }
                              })}
                            />
                            <label className="form-check-label small">
                              Compter les interactions
                            </label>
                          </div>
                        </div>
                      </div>
                     </div>
                  </div>
                 </div>
              );
              })}
             </div>
             <div className="d-flex gap-2 mt-3 flex-wrap">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => updateConfig({...defaultConfig, ...config})}>🔄 Réinitialiser avec exemple</button>
               <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => {
                  const advancedQuestions: SurveyQuestion[] = [
                    {
                      id: 'q1_name',
                      type: 'text',
                      title: 'Quel est votre nom ?',
                      required: true,
                      validation: { min: 2, max: 50 },
                      analytics: { trackTime: true, trackInteractions: true }
                    },
                    {
                      id: 'q2_satisfaction',
                      type: 'scale',
                      title: 'À quel point êtes-vous satisfait de nos services ?',
                      required: true,
                      scaleMin: 1,
                      scaleMax: 10,
                      scaleLabels: { min: 'Pas du tout satisfait', max: 'Très satisfait' },
                      analytics: { trackTime: true, trackInteractions: true }
                    },
                    {
                      id: 'q3_recommendation',
                      type: 'radio',
                      title: 'Recommanderiez-vous nos services ?',
                      required: true,
                      options: ['Oui, certainement', 'Probablement', 'Peut-être', 'Probablement pas', 'Non, certainement pas'],
                      analytics: { trackTime: true, trackInteractions: true }
                    },
                    {
                      id: 'q4_reason',
                      type: 'textarea',
                      title: 'Pourquoi avez-vous donné cette note ?',
                      required: false,
                      conditionalLogic: {
                        dependsOn: 'q2_satisfaction',
                        showIf: 'less_than',
                        value: '6'
                      },
                      validation: { min: 10, max: 500 },
                      analytics: { trackTime: true, trackInteractions: true }
                    },
                    {
                      id: 'q5_contact',
                      type: 'email',
                      title: 'Votre adresse email (optionnelle)',
                      required: false,
                      placeholder: 'exemple@email.com',
                      analytics: { trackTime: true, trackInteractions: true }
                    }
                  ];
                  
                  updateConfig({
                    ...config,
                    questions: advancedQuestions,
                    title: 'Enquête de satisfaction avancée',
                    description: 'Questionnaire avec logique conditionnelle et validation avancée',
                    enableAnalytics: true,
                    enableExport: true,
                    autoSave: true
                  });
                }}
               >
                 ⭐ Exemple avancé
               </button>
               <button type="button" className="btn btn-sm btn-outline-info" onClick={() => {
                   const matrixExample: SurveyQuestion = {
                    id: `matrix_${Date.now()}`,
                    type: 'matrix',
                    title: 'Évaluez nos différents services',
                    required: true,
                    matrixRows: ['Qualité du service', 'Rapidité', 'Prix', 'Support client'],
                    matrixColumns: ['Excellent', 'Bon', 'Moyen', 'Mauvais'],
                    analytics: { trackTime: true, trackInteractions: true }
                  };
                  const questions = [...(currentConfig.questions || []), matrixExample];
                  updateConfig({ questions });
               }}>📊 Ajouter matrice</button>
               <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => {
                   const phoneQuestion: SurveyQuestion = {
                    id: `phone_${Date.now()}`,
                    type: 'phone',
                    title: 'Votre numéro de téléphone',
                    required: false,
                    placeholder: '06 12 34 56 78',
                    analytics: { trackTime: true, trackInteractions: true }
                  };
                  const questions = [...(currentConfig.questions || []), phoneQuestion];
                  updateConfig({ questions });
               }}>📱 Ajouter téléphone</button>
             </div>
          </div>
        )}

        {/* ========================================================================== */}
        {/* ONGLET "FONCTIONNALITÉS AVANCÉES"                                          */}
        {/* ========================================================================== */}
        {activeTab === 'advanced' && (
          <div>
            {/* Section Analytics */}
            <div className="card mb-3">
               <div className="card-body">
                <h6 className="card-title">📊 Analytics et statistiques</h6>
                <p className="card-subtitle mb-2 text-muted small">Suivi détaillé des réponses et du comportement</p>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={currentConfig.enableAnalytics !== false}
                    onChange={(e) => updateConfig({ enableAnalytics: e.target.checked })}
                  />
                  <label className="form-check-label">
                    Activer les analytics
                  </label>
                </div>
               </div>
            </div>

            {/* Section Export */}
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title">💾 Export des résultats</h6>
                <p className="card-subtitle mb-2 text-muted small">Export CSV/Excel des réponses</p>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={currentConfig.enableExport !== false}
                    onChange={(e) => updateConfig({ enableExport: e.target.checked })}
                  />
                  <label className="form-check-label">
                    Activer l'export des résultats
                  </label>
                </div>
              </div>
            </div>

            {/* Section Notifications */}
            <div className="card mb-3">
               <div className="card-body">
                <h6 className="card-title">🔔 Notifications par email</h6>
                 <p className="card-subtitle mb-2 text-muted small">Alerte lors de nouvelles réponses</p>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={currentConfig.enableNotifications || false}
                    onChange={(e) => updateConfig({ enableNotifications: e.target.checked })}
                  />
                  <label className="form-check-label">
                    Activer les notifications par email
                  </label>
                </div>
                 {currentConfig.enableNotifications && (
                  <div className="mt-2">
                    <label className="form-label">Email de notification</label>
                    <input
                      type="email"
                      className="form-control"
                      value={currentConfig.notificationEmail || ''}
                      onChange={(e) => updateConfig({ notificationEmail: e.target.value })}
                      placeholder="admin@exemple.com"
                    />
                  </div>
                 )}
               </div>
            </div>

            {/* Section Sécurité */}
            <div className="card">
               <div className="card-body">
                 <h6 className="card-title">🛡️ Protection et validation</h6>
                 <p className="card-subtitle mb-2 text-muted small">Protection anti-spam</p>
                 
                 <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={currentConfig.enableAntiSpam || false}
                    onChange={(e) => updateConfig({ enableAntiSpam: e.target.checked })}
                    id="enableAntiSpam"
                  />
                  <label className="form-check-label" htmlFor="enableAntiSpam">
                    Protection anti-spam
                  </label>
                </div>
                 <div className="mb-3" style={{ maxWidth: 240 }}>
                  <label className="form-label">Délai minimum avant soumission (s)</label>
                  <input
                    type="number"
                    className="form-control"
                    min={0}
                    value={currentConfig.antiSpamMinSeconds ?? 5}
                    onChange={(e) => updateConfig({ antiSpamMinSeconds: Math.max(0, Number(e.target.value || 0)) })}
                  />
                </div>
                 <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={currentConfig.requireCaptcha || false}
                    onChange={(e) => updateConfig({ requireCaptcha: e.target.checked })}
                    id="requireCaptcha"
                  />
                   <label className="form-check-label" htmlFor="requireCaptcha">
                    Captcha obligatoire
                  </label>
                 </div>
                 
                 <div className="row mt-3">
                   <div className="col-md-6">
                    <label className="form-label">Date d'expiration</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={currentConfig.expirationDate ? new Date(currentConfig.expirationDate).toISOString().slice(0, 16) : ''}
                      onChange={(e) => updateConfig({ expirationDate: e.target.value ? new Date(e.target.value) : undefined })}
                    />
                    <small className="text-muted">Optionnel</small>
                   </div>
                 </div>
               </div>
            </div>
          </div>
        )}
        
        {/* ========================================================================== */}
        {/* ONGLET "RÉSULTATS"                                                         */}
        {/* ========================================================================== */}
        {activeTab === 'results' && (
           <div className="bg-white border rounded">
              <SurveyResults config={currentConfig} />
           </div>
        )}
      </div>
    </div>
  );
};

// Composant de prévisualisation qui utilise directement le plugin legacy
const SurveyPreviewComponent: React.FC<PluginPreviewProps> = ({ config, t }) => {
  if ((config.questions || []).length === 0) {
    return (
      <div className="text-center text-muted p-4">
        <div className="mb-2">
          <span className="badge bg-info fs-4 p-3">📝</span>
        </div>
        <h6>Questionnaire vide</h6>
        <p className="small">Ajoutez des questions pour voir la prévisualisation</p>
      </div>
    );
  }

  const extConfig = config as ExtendedSurveyConfig;
  const textColor = extConfig.textColor || '#333333';

  // ✅ Application des nouveaux styles : Fond, Texte, Arrondi, Responsive
  const containerStyle: React.CSSProperties = {
    color: textColor,
    backgroundColor: extConfig.backgroundTransparent ? 'transparent' : (extConfig.backgroundColor || '#ffffff'),
    borderRadius: `${extConfig.borderRadius || 0}px`,
    width: extConfig.responsive ? '100%' : 'auto',
    overflow: 'hidden', // Pour respecter le border-radius
    padding: extConfig.backgroundTransparent ? '0' : '1rem', // Un peu de padding si fond coloré
    position: 'relative' // Nécessaire pour le scope du style
  };

  // On ajoute une classe unique pour scoper le CSS
  const wrapperClass = `survey-wrapper-${Date.now()}`;

  // On enveloppe le plugin legacy pour appliquer nos styles "Custom"
  return (
    <div style={containerStyle} className={wrapperClass}>
      {/* 
         ✅ CORRECTION : Injection de style pour forcer la couleur sur les éléments internes 
         (Bootstrap .card, headings, labels réinitialisent souvent la couleur)
      */}
      <style>{`
        /* ✅ MODIFICATION : On force le fond transparent sur les cartes Bootstrap internes */
        .${wrapperClass} .card {
          background-color: transparent !important;
          box-shadow: none !important;
          border: none !important;
        }

        /* On applique aussi la transparence aux listes si nécessaire, ou on les laisse pour le contraste */
        .${wrapperClass} .list-group-item {
          background-color: transparent !important;
          border-color: rgba(0,0,0,0.1);
        }

        .${wrapperClass} .card,
        .${wrapperClass} h1, .${wrapperClass} h2, .${wrapperClass} h3, 
        .${wrapperClass} h4, .${wrapperClass} h5, .${wrapperClass} h6,
        .${wrapperClass} p, .${wrapperClass} label, .${wrapperClass} span,
        .${wrapperClass} .form-check-label {
          color: ${textColor} !important;
        }
        /* Gestion spécifique pour le texte muted pour qu'il reste lisible mais dans la teinte */
        .${wrapperClass} .text-muted {
          color: ${textColor} !important;
          opacity: 0.6;
        }
      `}</style>
      <SurveyPlugin config={config} />
    </div>
  );
};

// Plugin principal
export const SurveyPluginModular: PluginInterface = {
  metadata: {
    id: 'survey-modular',
    name: 'Questionnaire',
    description: 'Créez des questionnaires et sondages interactifs avec différents types de questions',
    category: 'interaction',
    version: '1.0.0',
    author: 'Book Editor',
    icon: '📊',
    tags: ['questionnaire', 'sondage', 'formulaire', 'enquête', 'interaction']
  },

  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  // ✅ MISE À JOUR MAJEURE : Documentation pour l'Architecte IA (ContainerWizard)
  documentationForAI: {
    description: "Générateur de formulaires avancés, sondages et enquêtes avec logique conditionnelle.",
    usage: "Utilise ce plugin quand l'utilisateur demande un 'sondage', 'questionnaire', 'formulaire de contact' ou 'enquête de satisfaction'.",
    
    configSchema: {
      type: "object",
      properties: {
        title: { type: "string", description: "Titre principal du formulaire" },
        description: { type: "string", description: "Sous-titre ou instructions" },
        submitButtonText: { type: "string", default: "Envoyer" },
        mode: { type: "enum: survey, game, exam", description: "Mode de fonctionnement (sondage/jeu/examen)" },
        pointsGoal: { type: "number", description: "Objectif de points (mode jeu)" },
        examScoreScale: { type: "number", description: "Note sur X (mode examen)" },
        uiStylePrompt: { type: "string", description: "Prompt pour guider le style visuel via IA" },
        uiStyleModel: { type: "string", description: "Modèle IA utilisé pour le style (selon clés disponibles)" },
        
        // Style
        textColor: { type: "string (hex)", description: "Couleur des textes" },
        backgroundColor: { type: "string (hex)", description: "Couleur de fond" },
        backgroundTransparent: { type: "boolean" },
        borderRadius: { type: "number", description: "Arrondi des bords (0-30)" },
        
        // Fonctionnalités
        enableAnalytics: { type: "boolean", description: "Activer le suivi des temps et réponses" },
        enableAntiSpam: { type: "boolean", description: "Protection contre les bots" },
        
        questions: {
          type: "array",
          items: {
            id: "string (unique)",
            type: "enum: text, textarea, email, phone, rating, radio, checkbox, select, date, number, matrix",
            title: "string",
            required: "boolean",
            options: "array<string> (Requis pour radio/checkbox/select)",
            correctAnswers: "array<string> (Bonnes réponses pour le scoring)",
            points: "number (Points attribués si réponse correcte)",
            scoringMode: "enum: all_or_nothing, partial (checkbox)",
            partialPointsPerCorrect: "number (Points par bonne case)",
            partialPointsPerWrong: "number (Malus par mauvaise case)",
            
            // Logique conditionnelle
            conditionalLogic: {
              description: "Pour afficher cette question UNIQUEMENT si une autre réponse correspond",
              properties: {
                dependsOn: "ID de la question parente",
                showIf: "enum: equals, not_equals, greater_than, less_than, selected",
                value: "Valeur déclencheur"
              }
            },
            
            // Validation
            validation: {
              properties: {
                min: "number (min value or min length)",
                max: "number",
                customMessage: "string"
              }
            }
          }
        }
      }
    } as any, // ✅ CAST AJOUTÉ : 'as any' pour autoriser la structure complexe JSON Schema malgré le type strict
    
    // Exemple complexe pour apprendre à l'IA la structure
    exampleConfig: {
      title: "Examen Client 2026",
      description: "Votre avis compte pour nous.",
      mode: "exam",
      examScoreScale: 20,
      uiStylePrompt: "Mode examen, style sobre, couleurs neutres",
      uiStyleModel: "dall-e-3",
      textColor: "#333333",
      backgroundColor: "#ffffff",
      borderRadius: 12,
      enableAnalytics: true,
      questions: [
        { 
          id: "q_sat", 
          type: "rating", 
          title: "Note globale", 
          required: true, 
          ratingMax: 5 
        },
        { 
          id: "q_details", 
          type: "textarea", 
          title: "Dites-nous pourquoi ?", 
          required: true,
          // Exemple de logique conditionnelle
          conditionalLogic: {
            dependsOn: "q_sat",
            showIf: "less_than",
            value: "3"
          }
        },
        {
          id: "q_contact",
          type: "radio",
          title: "Souhaitez-vous être recontacté ?",
          options: ["Oui", "Non"],
          correctAnswers: ["Oui"],
          points: 2
        },
        {
          id: "q_email",
          type: "email",
          title: "Votre email",
          required: true,
          // Logique imbriquée
          conditionalLogic: {
            dependsOn: "q_contact",
            showIf: "equals",
            value: "Oui"
          }
        }
      ]
    }
  },

  defaultConfig: defaultConfig,
  
  ConfigComponent: SurveyConfigComponent,
  
  PreviewComponent: SurveyPreviewComponent,
  
  generateHTML: (config: SurveyConfig) => {
    // Génération d'un ID unique par instance
    const instanceId = (config as any).id || `survey-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    const configWithId = { ...config, id: instanceId };

    const questionCount = (config.questions || []).length;
    const questionsText = questionCount === 0 ? 'Aucune question' : 
      questionCount === 1 ? '1 question' : `${questionCount} questions`;
    
    return generateUniformPlaceholder({
      title: config.title || 'Nouveau Questionnaire',
      description: `📊 ${questionsText} • Questionnaire interactif`,
      icon: '📊',
      category: 'interaction',
      config: configWithId,
      pluginId: instanceId,
      pluginType: 'survey-modular'
    });
  }
};

export default SurveyPluginModular;
